import React, { useState, useMemo, useEffect, useRef } from 'react'
import { Box, Stack } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import Tree from 'packages/ui/Arborist/components/Tree'
import CardSelection from 'packages/ui/Arborist/components/CardSelection'
import TabPanel from 'components/global/TabPanel'
import { toast } from 'components/global/CUK/Toast/Toast'
import TreeSearch from 'packages/ui/Arborist/components/Search'
import { useTreeContext, useSitesSelected } from 'packages/ui/Arborist/TreeProvider'
import { useInPerimeter } from 'packages/ui/Arborist/utils'
import convertAccounts from 'packages/ui/Arborist/utils'
import SearchField from 'packages/ui/Arborist/components/Search/SearchField/SearchField'
import ButtonUnfold, { ButtonSites } from 'packages/ui/Arborist/components/Buttons'

import ActionButtons from '../ActionButtons'

const Accounts = ({ loading, onSubmit, onGoBack }) => {
  const [showSelection, setShowSelection] = useState(false)
  const {
    formState: { errors }
  } = useFormContext()
  const { accounts, selection, onNodeAdd, onNodeRemove, waiting, treeRef, isFolded, onFoldedToggle } = useTreeContext()
  const { sites: sitesSelected, count: nbSitesSelected } = useSitesSelected({ selection })
  const checkedAccounts = useInPerimeter()
  const [searchTerm, setSearchTerm] = useState('')
  const { t } = useTranslation()
  const { register, setValue } = useFormContext()
  const searchFieldRef = useRef(null)
  const dataTree = useMemo(() => {
    return convertAccounts({ accounts })
  }, [accounts])
  const handleSubmit = () => {
    if (Object.entries(errors).length > 0) {
      toast.error(t('auth.admin.edit.form_errors'))
      return
    }
    if (nbSitesSelected === 0) {
      toast.error(t('auth.admin.edit.sites_selected_errors'))
      return
    }
    onSubmit()
  }
  useEffect(() => {
    setValue(
      'accounts',
      selection.filter(({ is_site }) => is_site).map(({ sf_id }) => sf_id),
      {
        shouldValidate: true,
        shouldDirty: true
      }
    )
  }, [selection])
  return (
    <TabPanel sx={{ flex: 1, height: '100%', minHeight: 0, width: '100%', overflow: 'hidden' }}>
      <input type={'hidden'} {...register('account_ids.parent_ids')} />
      <input type={'hidden'} {...register('account_ids.site_ids')} />
      <Stack mt={1} pb={2} spacing={2} sx={{ cursor: waiting ? 'wait' : 'inherit', height: '100%', minHeight: 0 }}>
        <Stack spacing={2} sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
          <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
            <Stack direction="row" gap={2} sx={{ maxHeight: '40px' }}>
              <SearchField
                ref={searchFieldRef}
                onChange={setSearchTerm}
                showCountChip={false}
                onFocus={() => {
                  setShowSelection(false)
                }}
              />
              <ButtonSites
                active={showSelection}
                count={nbSitesSelected}
                onClick={() => setShowSelection(prev => !prev)}
              />
              <ButtonUnfold isUnfolded={!isFolded} onClick={onFoldedToggle} />
            </Stack>
            {searchTerm.length ? (
              <TreeSearch hideTopBar={true} ref={treeRef} name={searchTerm} />
            ) : (
              showSelection && (
                <CardSelection
                  nbSites={nbSitesSelected}
                  accounts={sitesSelected}
                  onRemoveAccount={account => {
                    onNodeRemove({ id: account.sf_id })
                  }}
                  onRemoveAccountParent={account => {
                    onNodeRemove({ id: account.sf_id })
                  }}
                />
              )
            )}
            {!showSelection && (
              <Stack sx={{ flex: 1, minHeight: 0 }}>
                {!searchTerm.length && (
                  <Box
                    sx={{
                      border: 'solid 1px',
                      borderColor: 'border.primary',
                      borderRadius: '8px',
                      height: '100%',
                      flex: 1
                    }}>
                    <Tree
                      ref={treeRef}
                      data={dataTree}
                      openByDefault
                      onNodeSelect={async node => {
                        const checked =
                          checkedAccounts.findIndex(account => account.sf_id === node.data.data.sf_id) > -1
                        if (!checked) {
                          if (!waiting) {
                            await onNodeAdd(node)
                          }
                        } else {
                          onNodeRemove(node)
                        }
                      }}
                    />
                  </Box>
                )}
              </Stack>
            )}
          </Stack>
        </Stack>
        <ActionButtons
          loading={loading}
          onGoBack={onGoBack}
          onSubmit={handleSubmit}
          labelSubmit={t('ui_elements.form_validate')}
        />
      </Stack>
    </TabPanel>
  )
}

export default Accounts
